/**
 * Field shell (U5): label / hint / error chrome shared by every form control.
 * Also exported for feature screens that need custom control layouts.
 */
import type { JSX, ReactNode } from 'react';
import { cx } from './cx';

export interface FieldProps {
  /** id of the control this label points at. */
  htmlFor?: string;
  label?: ReactNode;
  hint?: ReactNode;
  /** Truthy error replaces the hint and turns the field invalid. */
  error?: ReactNode;
  required?: boolean;
  full?: boolean;
  className?: string;
  children: ReactNode;
  /** id for the hint/error node (wire to aria-describedby on the control). */
  describedById?: string;
}

export function Field({
  htmlFor,
  label,
  hint,
  error,
  required,
  full,
  className,
  children,
  describedById,
}: FieldProps): JSX.Element {
  return (
    <div className={cx('bl-field', full && 'bl-field--full', className)}>
      {label != null && (
        <label className="bl-field__label" htmlFor={htmlFor}>
          {label}
          {required && (
            <span className="bl-field__req" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <div className="bl-field__error" id={describedById} role="alert">
          {error}
        </div>
      ) : hint != null ? (
        <div className="bl-field__hint" id={describedById}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}
